import React, { useEffect } from 'react';
import './TermsOfService.css';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom'; 

const TermsOfService = () => {
  const location = useLocation(); 

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    
    // Enable scrolling for this page
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    
    // Cleanup: restore original styles when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [location]);
  
  return (
    <div className="terms-of-service">
      <h1>Terms of Service for Ideal Scribe</h1>
      
      <section>
        <h2>1. Introduction</h2>
        <p>
          Thank you for choosing Ideal Scribe (referred to as "Ideal Scribe", "we", "our", or "us"). These Terms of Service establish the 
          legal framework governing your access and use of our platform available at https://www.idealscribe.com, along with all associated 
          features and services (collectively referred to as the "Platform").
        </p>
        <p>
          In addition to these Terms, our Privacy Policy outlines how we handle, protect, and share data collected through your interaction 
          with our Platform. You can review our Privacy Policy at https://www.idealscribe.com/privacy-policy.
        </p>
        <p>
          Together, these Terms and our Privacy Policy form the complete agreement ("Agreement") between you and Ideal Scribe. By accessing 
          or using our Platform, you confirm that you have reviewed, understood, and consent to be legally bound by this Agreement.
        </p>
        <p>
          Should you disagree with any portion of this Agreement or find yourself unable to comply with its requirements, you must refrain 
          from using the Platform. We encourage you to reach out to us at support@idealscribe.com if you have concerns, and we will work 
          together to address them. These Terms apply universally to all individuals who access or utilize our Platform.
        </p>
      </section>

      <section>
        <h2>2. User Accounts</h2>
        <p>
          To utilize certain features of our Platform, you must establish a user account. When creating your account, you commit to providing 
          truthful, up-to-date, and comprehensive information. Providing false, outdated, or incomplete information violates these Terms 
          and may lead to immediate account suspension or termination.
        </p>
        <p>
          Maintaining the security of your account credentials is your sole responsibility. You are accountable for all activities that occur 
          under your account, whether conducted through our Platform or through integrated third-party authentication services.
        </p>
        <p>
          You must keep your password confidential and not share it with others. If you become aware of any unauthorized access to your 
          account or any security breach, you must notify us immediately at support@idealscribe.com.
        </p>
        <p>
          When selecting a username, you may not use names that belong to other individuals or organizations, names that infringe on 
          intellectual property rights, or names that are offensive, inappropriate, or violate community standards.
        </p>
      </section>

      <section>
        <h2>3. Marketing Communications</h2>
        <p>
          Upon account creation, you consent to receive periodic communications from us, including newsletters, promotional offers, product 
          updates, and other informational content. You retain the right to unsubscribe from these communications at any time by clicking 
          the unsubscribe link in any email or by contacting us directly at support@idealscribe.com.
        </p>
      </section>

      <section>
        <h2>4. User-Generated Content</h2>
        <p>
          Our Platform enables you to create, upload, share, and manage various forms of content including text, images, location data, 
          travel notes, and other materials (collectively, "User Content"). You bear full responsibility for all User Content you create 
          or share through the Platform, including ensuring it complies with applicable laws and does not infringe on others' rights.
        </p>
        <p>
          When you submit User Content to our Platform, you affirm and guarantee that: (i) you own the content or possess all necessary 
          rights and permissions to use and share it, and to grant us the licenses described herein; and (ii) your User Content does not 
          violate any person's or entity's privacy rights, publicity rights, intellectual property rights, contractual rights, or any other 
          legal rights. We maintain the right to immediately suspend or terminate accounts of users who engage in copyright infringement.
        </p>
        <p>
          You maintain ownership of your User Content. We do not claim ownership of your User Content, and you are responsible for protecting 
          your rights. We disclaim all responsibility and liability for User Content posted by you or other users. However, by submitting User 
          Content through our Platform, you grant us a worldwide, non-exclusive, royalty-free license to use, adapt, publish, display, 
          reproduce, and share your User Content within and through the Platform. This license permits us to make your User Content 
          accessible to other Platform users, who may interact with it in accordance with these Terms.
        </p>
        <p>
          We retain the right, though not the obligation, to review, moderate, and remove User Content at our discretion.
        </p>
        <p>
          All content, design elements, and materials found on or accessible through our Platform are owned by Ideal Scribe or used with 
          proper authorization. You may not reproduce, distribute, modify, create derivative works from, publicly display, or commercially 
          exploit any Platform content without obtaining our express written permission in advance.
        </p>
        
        <h3>Restricted Content</h3>
        <p>
          The following types of content are strictly prohibited on our Platform:
        </p>
        <ul>
          <li>Content that is defamatory, discriminatory, hateful, or targets individuals or groups based on religion, race, ethnicity, 
          sexual orientation, gender identity, or other protected characteristics in a manner intended to harass, intimidate, or cause harm.</li>
          <li>Graphic depictions of violence, cruelty, or abuse toward people or animals, or content that promotes or glorifies violence.</li>
          <li>Content that promotes illegal activities, dangerous behaviors, or the unlawful use of weapons or hazardous materials.</li>
          <li>Sexually explicit or pornographic material, which we define as content primarily intended to arouse sexual desire rather than 
          serve artistic, educational, or informational purposes.</li>
          <li>Misleading religious content or misrepresentations of religious texts or beliefs designed to incite conflict.</li>
          <li>Deliberately false or misleading information, including fabricated location data or deceptive content presented as factual.</li>
        </ul>
        <p>
          If you encounter content that violates these guidelines, please report it to us at support@idealscribe.com.
        </p>
      </section>

      <section>
        <h2>5. Acceptable Use Policy</h2>
        <p>
          You agree to use our Platform only for lawful purposes and in compliance with these Terms. The following activities are expressly 
          prohibited:
        </p>
        <ul>
          <li>Using the Platform in any manner that violates local, state, national, or international laws or regulations.</li>
          <li>Targeting, exploiting, or endangering minors through the Platform or exposing them to harmful content.</li>
          <li>Transmitting unsolicited commercial communications, spam, chain letters, or other forms of unwanted promotional content.</li>
          <li>Impersonating Ideal Scribe, our employees, other users, or any other person or organization.</li>
          <li>Engaging in activities that violate others' rights, are illegal, threatening, deceptive, or harmful, or that facilitate 
          unlawful conduct.</li>
          <li>Interfering with or disrupting the Platform's operation, security, or other users' ability to access and use the Platform.</li>
        </ul>
      </section>

      <section>
        <h2>6. Artificial Intelligence Services</h2>
        <p>
          Ideal Scribe incorporates artificial intelligence technology to deliver enhanced functionality such as intelligent note organization, 
          personalized recommendations, and automated travel planning tools. By using these AI-powered features, you understand and accept that:
        </p>
        <ul>
          <li>AI-generated outputs and recommendations are provided without warranties and may contain inaccuracies or be unsuitable for 
          your specific needs.</li>
          <li>You must independently verify and evaluate any AI-generated content before making decisions based on it.</li>
          <li>We make no guarantees regarding the precision, thoroughness, or practical value of AI-generated content.</li>
          <li>Our AI systems may utilize your User Content to enhance and refine our services, consistent with our Privacy Policy.</li>
        </ul>
      </section>

      <section>
        <h2>7. Email-to-Note Functionality</h2>
        <p>
          Ideal Scribe offers the ability to create notes by sending emails to scribe@quicknotes.idealscribe.com. When using this feature, 
          you agree to the following:
        </p>
        <ul>
          <li>You will only send emails containing content for which you have proper authorization and rights.</li>
          <li>We may process, store, and utilize email content in accordance with our Privacy Policy.</li>
          <li>We are not liable for emails that fail to be received or processed due to technical difficulties, spam filters, or other 
          delivery issues.</li>
          <li>You will not abuse this feature by sending spam, malicious content, or content that breaches these Terms.</li>
        </ul>
      </section>

      <section>
        <h2>8. Geographic Data and Mapping Services</h2>
        <p>
          Our Platform integrates Google Maps and related geographic services to provide mapping and location-based functionality. By using 
          these features, you agree to abide by Google's applicable Terms of Service and Privacy Policy regarding map data usage.
        </p>
        <p>
          You understand and accept that:
        </p>
        <ul>
          <li>Geographic and location data are sourced from third-party providers and may contain errors or be outdated.</li>
          <li>We are not responsible for inaccuracies in geographic data or location information.</li>
          <li>You should independently verify location accuracy before relying on it for travel planning or other important decisions.</li>
        </ul>
      </section>

      <section>
        <h2>9. Subscription Plans</h2>
        <p>
          Certain Platform features are available through paid subscription plans ("Subscriptions"). Subscriptions are billed on a recurring 
          basis, either monthly or annually, depending on the plan you select ("Billing Period").
        </p>
        <p>
          Unless you cancel your Subscription or we terminate it, your Subscription will automatically renew at the end of each Billing Period 
          under the same terms. You can cancel your Subscription at any time through your account settings or by contacting our support team.
        </p>
        <p>
          To maintain an active Subscription, you must provide a valid payment method (such as a credit card). You agree to supply accurate 
          and current billing details, including your full name, mailing address, phone number, and payment information. By providing payment 
          information, you authorize Ideal Scribe to charge your payment method for all Subscription fees associated with your account.
        </p>
        <p>
          If automatic payment processing fails for any reason, we will send you an electronic invoice requiring manual payment by a specified 
          due date for the applicable Billing Period.
        </p>
      </section>

      <section>
        <h2>10. Trial Periods</h2>
        <p>
          At our discretion, Ideal Scribe may offer time-limited trial access to Subscription features ("Trial Period").
        </p>
        <p>
          To begin a Trial Period, you may need to provide billing information.
        </p>
        <p>
          If billing information is provided during Trial Period registration, you will not be charged until the Trial Period ends. On the 
          final day of your Trial Period, unless you have cancelled, we will automatically charge your payment method the applicable 
          Subscription fee for your selected plan.
        </p>
        <p>
          We reserve the right to modify or discontinue Trial Period offers at any time without prior notice.
        </p>
      </section>

      <section>
        <h2>11. Subscription Pricing Modifications</h2>
        <p>
          Ideal Scribe may adjust Subscription pricing at any time at our sole discretion. Price changes will take effect at the conclusion 
          of your current Billing Period.
        </p>
        <p>
          We will provide reasonable advance notice of any pricing changes, giving you the opportunity to cancel your Subscription before 
          the new pricing takes effect.
        </p>
        <p>
          Continued use of the Platform after a price change becomes effective indicates your acceptance of the new pricing structure.
        </p>
      </section>

      <section>
        <h2>12. Refund Policy</h2>
        <p>
          Subscription fees are generally non-refundable, except where required by applicable law.
        </p>
      </section>

      <section>
        <h2>13. Usage Analytics</h2>
        <p>
          We partner with third-party analytics providers to track and analyze Platform usage patterns. These services may gather data such 
          as IP addresses, device types, pages accessed, session duration, and interaction patterns. This information helps us improve the 
          Platform and is handled according to our Privacy Policy.
        </p>
      </section>

      <section>
        <h2>14. External Links and Third-Party Services</h2>
        <p>
          Our Platform may include links to websites, applications, or services operated by third parties that are not affiliated with 
          Ideal Scribe.
        </p>
        <p>
          We do not control, endorse, or assume responsibility for the content, privacy practices, or terms of service of third-party 
          websites or services. We make no representations or warranties regarding third-party offerings.
        </p>
        <p>
          You acknowledge that Ideal Scribe shall not be liable for any damages or losses arising from your use of or reliance on third-party 
          content, products, or services accessed through external links.
        </p>
        <p>
          We strongly recommend reviewing the terms of service and privacy policies of any third-party websites or services you visit.
        </p>
      </section>

      <section>
        <h2>15. Service Warranties</h2>
        <p>
          The Platform is provided on an "as is" and "as available" basis. Ideal Scribe makes no representations or warranties, whether 
          express or implied, regarding the Platform's operation, content, or the information and materials it contains. Your use of the 
          Platform, its content, and any services or materials obtained through it is at your own risk.
        </p>
        <p>
          Neither Ideal Scribe nor anyone associated with us makes any warranty or representation concerning the completeness, security, 
          reliability, quality, accuracy, or availability of the Platform. Without limiting the foregoing, we do not warrant that the Platform 
          will be accurate, reliable, error-free, uninterrupted, free of viruses or other harmful components, or that defects will be corrected.
        </p>
        <p>
          Ideal Scribe disclaims all warranties of any kind, whether express, implied, statutory, or otherwise, including but not limited to 
          warranties of merchantability, non-infringement, and fitness for a particular purpose.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you.
        </p>
      </section>

      <section>
        <h2>16. Liability Limitations</h2>
        <p>
          To the maximum extent permitted by law, you agree to indemnify and hold harmless Ideal Scribe, our officers, directors, employees, 
          and agents from any indirect, punitive, special, incidental, or consequential damages, including attorneys' fees and related legal 
          costs, arising from your use of the Platform, violation of these Terms, or violation of any laws or regulations. This applies 
          regardless of whether such damages arise from contract, negligence, tort, or other legal theory, and even if we have been advised 
          of the possibility of such damages.
        </p>
        <p>
          To the extent permitted by law, if Ideal Scribe is found liable, our total liability shall be limited to the amount you paid for 
          the products or services giving rise to the claim. In no event shall we be liable for consequential or punitive damages. Some 
          jurisdictions do not allow limitations on incidental or consequential damages, so these limitations may not apply to you.
        </p>
      </section>

      <section>
        <h2>17. Account Termination</h2>
        <p>
          We reserve the right to suspend or terminate your account and restrict your access to the Platform immediately, without prior notice, 
          at our sole discretion, for any reason, including but not limited to violations of these Terms.
        </p>
        <p>
          If you wish to close your account, you may stop using the Platform or contact us at support@idealscribe.com to request account 
          deletion.
        </p>
        <p>
          Provisions of these Terms that by their nature should survive termination will remain in effect after termination, including 
          ownership rights, warranty disclaimers, indemnification obligations, and liability limitations.
        </p>
      </section>

      <section>
        <h2>18. Legal Jurisdiction</h2>
        <p>
          These Terms are governed by and interpreted in accordance with United States federal law and applicable state laws, without regard to 
          conflict of law principles.
        </p>
        <p>
          Our failure to enforce any provision of these Terms does not constitute a waiver of that provision. If any provision is found to 
          be invalid or unenforceable by a court, the remaining provisions will remain in full force and effect. These Terms represent the 
          complete agreement between you and Ideal Scribe regarding the Platform and supersede all prior agreements and understandings.
        </p>
      </section>

      <section>
        <h2>19. Platform Modifications</h2>
        <p>
          We reserve the right to modify, suspend, or discontinue any aspect of the Platform, or any services or materials provided through 
          it, at any time without notice. We will not be liable if the Platform or any portion of it becomes unavailable at any time or for 
          any period. We may also restrict access to certain Platform features or the entire Platform, including to registered users, at our 
          discretion.
        </p>
      </section>

      <section>
        <h2>20. Terms Updates</h2>
        <p>
          We may update these Terms at any time by posting revised terms on this page. It is your responsibility to periodically review 
          these Terms to stay informed of any changes.
        </p>
        <p>
          Your continued use of the Platform after revised Terms are posted indicates your acceptance of the changes. You should check this 
          page regularly to be aware of updates, as they are binding on you.
        </p>
        <p>
          If you continue to access or use the Platform after Terms revisions take effect, you agree to be bound by the revised Terms. If you 
          do not agree to the revised Terms, you must stop using the Platform.
        </p>
      </section>

      <section>
        <h2>21. Waiver and Enforceability</h2>
        <p>
          Any failure by Ideal Scribe to enforce any term or condition of these Terms does not constitute a waiver of that term or condition, 
          nor does it waive any other terms or conditions. Our failure to assert a right or provision does not waive that right or provision.
        </p>
        <p>
          If any provision of these Terms is determined by a court or other competent authority to be invalid, illegal, or unenforceable, 
          that provision will be modified to the minimum extent necessary to make it valid and enforceable, or eliminated entirely if 
          modification is not possible. The remaining provisions will continue in full force and effect.
        </p>
      </section>

      <section>
        <h2>22. Intellectual Property Rights</h2>
        <p>
          All original content, design elements, features, and functionality of the Platform are owned by Ideal Scribe and our licensors 
          and are protected by United States copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks, 
          service marks, and trade dress may not be used without our prior written permission.
        </p>
        <p>
          You recognize that the Platform contains proprietary and confidential information protected by intellectual property and other laws. 
          You agree not to copy, modify, create derivative works from, rent, lease, loan, sell, distribute, or exploit the Platform or any 
          portion of it in any unauthorized manner.
        </p>
      </section>

      <section>
        <h2>23. User Agreement</h2>
        <p>
          By accessing or using the Platform or any services we provide, you acknowledge that you have read, understood, and agree to be 
          bound by these Terms of Service.
        </p>
      </section>

      <section>
        <h2>24. Contact Information</h2>
        <p>
          For questions, feedback, or technical support requests, please contact us:
        </p>
        <p>
          Email: <a href="mailto:support@idealscribe.com">support@idealscribe.com</a>
        </p>
      </section>
    </div>
  );
};

export default TermsOfService;
